import type { ReactElement, ReactNode } from 'react';
import { Link } from '@repo/poly-hooks/components';
import type { To } from 'react-router';
import cx from 'clsx';
import { ActionSlot } from '../../../../modular-table/components/action-slot';
import styles from './widget-row.module.css';

export interface WidgetRowColumn {
  title: ReactNode;
  subtitle?: ReactNode;
  align?: 'start' | 'end';
  width?: number | string;
}

interface WidgetRowProps {
  avatar: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  columns?: WidgetRowColumn[];
  hoverActions?: ReactNode;
  redirection: To;
  onClick?: () => void;
}

function Title({ children, className }: { children: ReactNode; className?: string }): ReactElement {
  return <span className={cx('body-standard', styles.title, className)}>{children}</span>;
}

function Subtitle({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactElement {
  return <span className={cx('caption', styles.subtitle, className)}>{children}</span>;
}

function ColumnTitle({
  children,
  className,
  typography = 'caption',
}: {
  children: ReactNode;
  className?: string;
  typography?: 'caption' | 'body-standard';
}): ReactElement {
  return <span className={cx(typography, styles.columnTitle, className)}>{children}</span>;
}

function ColumnSubtitle({
  children,
  className,
  typography = 'body-standard',
}: {
  children: ReactNode;
  className?: string;
  typography?: 'caption' | 'body-standard';
}): ReactElement {
  return <span className={cx(typography, styles.columnSubtitle, className)}>{children}</span>;
}

export function WidgetRowComponent({
  avatar,
  title,
  subtitle,
  columns,
  hoverActions,
  redirection,
  onClick,
}: WidgetRowProps): ReactNode {
  return (
    <li className={styles.item}>
      <Link
        className={styles.link}
        data-testid="widget-row-link"
        onClick={onClick}
        to={redirection}
      >
        <div className={styles.avatar}>{avatar}</div>
        <div className={styles.main}>
          {title}
          {subtitle ?? null}
        </div>
        {columns && columns.length > 0 ? (
          <div className={styles.columns}>
            {columns.map((column, index) => (
              <div
                className={cx(styles.column, column.align === 'end' && styles.columnEnd)}
                // oxlint-disable-next-line react/no-array-index-key
                key={index}
                style={column.width !== undefined ? { width: column.width } : undefined}
              >
                {column.title}
                {column.subtitle}
              </div>
            ))}
          </div>
        ) : null}
      </Link>
      {hoverActions ? (
        <ActionSlot className={styles.hoverActions} size="28">
          {hoverActions}
        </ActionSlot>
      ) : null}
    </li>
  );
}

export const WidgetRow = Object.assign(WidgetRowComponent, {
  Title,
  Subtitle,
  ColumnTitle,
  ColumnSubtitle,
});
